<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { DatePicker, IconImage, FilterModal, ThemeProvider, SelectInput } from '@/shared/ui';
  import ArrowRight from '@/shared/assets/icons/ArrowRight.svg';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { useFetchMembers } from '@/entities/Branch';
  import { useDocumentFilterModal } from '@/features/Document';
  import { useFilterStore } from '@/entities/Filter';
  import { formatCollaboratorNameAndRegistration } from '@/shared/lib';
  import type { FilterOptions } from '@/entities/Document';

  const props = defineProps<{
    currentFilters?: FilterOptions;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: FilterOptions): void;
    (e: 'update:selectedDate', date: Date | null): void;
    (e: 'clear-filters'): void;
  }>();

  const { fetchAllMembers, members } = useFetchMembers();
  const filterStore = useFilterStore();

  const {
    formData,
    elaborationDate,
    approvalDate,
    verificationDate,
    effectiveDate,
    isElaborationDateOpen,
    isApprovalDateOpen,
    isVerificationDateOpen,
    isEffectiveDateOpen,
    formatDate,
    clearFilters,
    applyFilters,
    loadFilters,
  } = useDocumentFilterModal(
    emit,
    fetchAllMembers,
    (props.currentFilters || filterStore.filters) as Partial<FilterOptions>
  );

  watch(
    () => props.currentFilters,
    (newFilters) => {
      if (newFilters && Object.keys(newFilters).length > 0) {
        loadFilters(newFilters as Partial<FilterOptions>);
      }
    },
    { deep: true }
  );

  const usersList = computed(() => {
    const memberData = members.value?.data || [];
    return memberData.map((member) => ({
      userId: member.userId?.toString() || member.id?.toString() || '',
      memberId: member.memberId?.toString() || member.id?.toString() || '',
      name: formatCollaboratorNameAndRegistration(member.name, member.registrationNumber, {
        labelWhenNameMissing: 'Nome não disponível',
      }),
    }));
  });

  const elaboratedByOptions = computed(() =>
    usersList.value.map((user) => ({
      id: user.userId,
      name: user.name,
    }))
  );

  const approvalMemberOptions = computed(() =>
    usersList.value.map((user) => ({
      id: user.memberId,
      name: user.name,
    }))
  );

  const safeFormData = computed(() => ({
    ...formData.value,
    elaboratedBy: formData.value.elaboratedBy ?? '',
    approvedBy: formData.value.approvedBy ?? '',
    verifiedBy: formData.value.verifiedBy ?? '',
  }));

  const handleClearFilters = () => {
    clearFilters();
    emit('clear-filters');
  };
</script>

<template>
  <FilterModal
    @close="emit('close')"
    @clear-filters="handleClearFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div class="px-6 space-y-4">
          <!-- Elaborado por -->
          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-2">
              Elaborado por
            </h4>
            <SelectInput
              :model-value="safeFormData.elaboratedBy"
              :options="elaboratedByOptions"
              label="Selecione um colaborador"
              @update:model-value="formData.elaboratedBy = $event"
            />
          </div>

          <!-- Aprovado por -->
          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-2">
              Aprovado por
            </h4>
            <SelectInput
              :model-value="safeFormData.approvedBy"
              :options="approvalMemberOptions"
              label="Selecione um colaborador"
              @update:model-value="formData.approvedBy = $event"
            />
          </div>

          <!-- Verificado por -->
          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-2">
              Verificado por
            </h4>
            <SelectInput
              :model-value="safeFormData.verifiedBy"
              :options="approvalMemberOptions"
              label="Selecione um colaborador"
              @update:model-value="formData.verifiedBy = $event"
            />
          </div>

          <!-- Data de Elaboração e Data de Aprovação -->
          <div class="py-4 border-b border-gray-02">
            <div class="flex gap-4">
              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 mb-2">
                  Data da Elaboração
                </h4>
                <button
                  class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-gray-07 font-medium gap-2 w-full"
                  @click="isElaborationDateOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-5 w-5"
                  />
                  <span class="grow text-center">{{
                    elaborationDate !== null ? formatDate(elaborationDate) : 'Selecione uma data'
                  }}</span>
                </button>
                <DatePicker
                  v-model:selected-date="elaborationDate"
                  v-model:is-open="isElaborationDateOpen"
                  @apply="isElaborationDateOpen = false"
                  @cancel="isElaborationDateOpen = false"
                />
              </div>
              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 mb-2">
                  Data de Aprovação
                </h4>
                <button
                  class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-gray-07 font-medium gap-2 w-full"
                  @click="isApprovalDateOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-5 w-5"
                  />
                  <span class="grow text-center">{{
                    approvalDate !== null ? formatDate(approvalDate) : 'Selecione uma data'
                  }}</span>
                </button>
                <DatePicker
                  v-model:selected-date="approvalDate"
                  v-model:is-open="isApprovalDateOpen"
                  @apply="isApprovalDateOpen = false"
                  @cancel="isApprovalDateOpen = false"
                />
              </div>
            </div>
          </div>

          <!-- Data de Verificação e Vigência -->
          <div class="py-4 border-b border-gray-02">
            <div class="flex gap-4">
              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 mb-2">
                  Data de Verificação
                </h4>
                <button
                  class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-gray-07 font-medium gap-2 w-full"
                  @click="isVerificationDateOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-5 w-5"
                  />
                  <span class="grow text-center">{{
                    verificationDate !== null ? formatDate(verificationDate) : 'Selecione uma data'
                  }}</span>
                </button>
                <DatePicker
                  v-model:selected-date="verificationDate"
                  v-model:is-open="isVerificationDateOpen"
                  @apply="isVerificationDateOpen = false"
                  @cancel="isVerificationDateOpen = false"
                />
              </div>
              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 mb-2">
                  Vigência
                </h4>
                <button
                  class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-gray-07 font-medium gap-2 w-full"
                  @click="isEffectiveDateOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-5 w-5"
                  />
                  <span class="grow text-center">{{
                    effectiveDate !== null ? formatDate(effectiveDate) : 'Selecione uma data'
                  }}</span>
                </button>
                <DatePicker
                  v-model:selected-date="effectiveDate"
                  v-model:is-open="isEffectiveDateOpen"
                  @apply="isEffectiveDateOpen = false"
                  @cancel="isEffectiveDateOpen = false"
                />
              </div>
            </div>
          </div>

          <!-- Revisões -->
          <div class="py-4">
            <div class="w-1/4">
              <h4 class="font-medium text-gray-07 mb-2">
                Revisões
              </h4>
              <div class="relative">
                <input
                  v-model.number="formData.revision"
                  type="number"
                  class="w-full px-4 py-2 border border-gray-02 rounded-lg text-gray-07 focus:outline-none focus:border-gray-07 [appearance:textfield] [-webkit-appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  min="0"
                >
                <div class="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col">
                  <IconImage
                    :src="ArrowRight"
                    alt="Aumentar"
                    class="h-6 w-6 rotate-270 cursor-pointer -ml-[1.52px]"
                    @click="formData.revision = (formData.revision ?? 0) + 1"
                  />
                  <IconImage
                    :src="ArrowRight"
                    alt="Diminuir"
                    class="h-6 w-6 rotate-90 cursor-pointer"
                    @click="
                      formData.revision =
                        (formData.revision ?? 0) > 0 ? (formData.revision ?? 0) - 1 : 0
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
